import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import earth from '@/assets/svg/earth.svg';
import sun from '@/assets/svg/sun.svg';
import wind from '@/assets/svg/wind.svg';
import { buttonVariants } from '@/components/app/button';
import { SectionWrapper } from '@/features/homepage/components/shared';
import { useIsRTL } from '@/hooks/i18n.hooks';
import { cn } from '@/lib/utils';

function Programs() {
	const { t } = useTranslation();

	return (
		<SectionWrapper heading={t('home.programs.heading')}>
			<div className="flex flex-col gap-6">
				<ProgramDetails
					trainers={t('home.programs.featured.air.trainers')}
					duration={t('home.programs.featured.air.duration')}
					title={t('home.programs.featured.air.title')}
					description={t('home.programs.featured.air.description')}
					buttonText={t('home.programs.featured.air.buttonText')}
					notice={t('home.programs.featured.air.notice', '')}
					path={'/programs/air'}
					image={wind}
				/>
				<ProgramDetails
					trainers={t('home.programs.featured.earth.trainers')}
					duration={t('home.programs.featured.earth.duration')}
					title={t('home.programs.featured.earth.title')}
					description={t('home.programs.featured.earth.description')}
					buttonText={t('home.programs.featured.earth.buttonText')}
					notice={t('home.programs.featured.earth.notice', '')}
					path={'/programs/earth'}
					image={earth}
				/>
				<ProgramDetails
					trainers={t('home.programs.featured.sun.trainers')}
					duration={t('home.programs.featured.sun.duration')}
					title={t('home.programs.featured.sun.title')}
					description={t('home.programs.featured.sun.description')}
					buttonText={t('home.programs.featured.sun.buttonText')}
					notice={t('home.programs.featured.sun.notice', '')}
					path={'/programs/sun'}
					image={sun}
				/>
			</div>
			<p className="mt-10 px-4">{t('home.programs.notice')}</p>
		</SectionWrapper>
	);
}

type ProgramDetailsProps = {
	trainers: string;
	duration: string;
	title: string;
	description: string;
	buttonText: string;
	notice: string;
	path: string;
	image: string;
};
function ProgramDetails(props: ProgramDetailsProps) {
	const isRTL = useIsRTL();
	return (
		<div className="group/program-details mt-4 grid grid-cols-[auto_1fr] grid-rows-[auto_1fr] px-4">
			<span className="-mb-[2px] col-start-2 row-start-1 justify-self-start border-2 border-ash-300 bg-ash-300 px-3 py-1">
				{props.trainers}
			</span>
			<div
				className={cn(
					'col-start-1 row-start-2 self-start border-2 border-ash-300 bg-white px-3 py-1 [writing-mode:vertical-rl]',
					isRTL ? '-ml-[2px]' : '-mr-[2px] rotate-180',
				)}
			>
				<span className="whitespace-nowrap">{props.duration}</span>
			</div>
			<div className="relative col-start-2 row-start-2 flex flex-col justify-between gap-4 border-2 border-ash-300 bg-white p-5">
				<div className="flex flex-nowrap items-center justify-start gap-x-4">
					<img src={props.image} className="w-12" alt={props.title} />
					<h3>{props.title}</h3>
				</div>
				<p>{props.description}</p>
				<div>
					<Link
						to={props.path}
						className={cn('float-end mt-4 bg-white', buttonVariants({ variant: 'secondary' }))}
					>
						{props.buttonText}
					</Link>
				</div>
				{props.notice !== '' && (
					<span
						className={cn(
							'-end-5 -top-6 absolute border-2 border-ash-400 bg-white px-3 py-1 shadow-xl transition-colors duration-150 group-hover/program-details:bg-pink-50',
							isRTL ? '-rotate-[25deg]' : 'rotate-[25deg]',
						)}
					>
						{props.notice}
					</span>
				)}
			</div>
		</div>
	);
}

export { Programs };
